import { api } from "@/app/app.api";
import { RequestMethod } from "@/shared/utils/RequestMethod";
import { buildQueryParams } from "@/shared/utils/utils";
// import { snakeToCamel } from "caseparser"; // No se usa directamente aquí a menos que transformes claves

// Define interfaces para las respuestas esperadas y los argumentos de las mutaciones
// Estas son ejemplos, ajústalas a la estructura real de tus datos

interface Lead {
  id: number;
  name: string;
  email: string;
  sex?: string;
  phone?: string;
  birthday?: string;
  comment?: string;
  campaign: boolean;
  branch_id: number;
  stages: string; // Etapa actual del lead
  notes?: string;
  // ... otros campos del lead
}

interface LeadTask {
  id: number;
  lead_id: number;
  description: string;
  due_date?: string;
  completed: boolean;
  // ... otros campos de la tarea
}

interface StageHistoryEntry {
  id: number;
  stages: string;
  user_id: number;
  lead_id: number;
  created_at: string;
  updated_at: string;
  // ... otros campos si los hay
}

interface AddNoteArgs {
  leadId: number | string;
  notes: string;
}

interface UpdateStageArgs {
  leadId: number | string;
  stages: string; // La nueva etapa
}

export enum SexFilter {
  Mujer = "Mujer",
  Hombre = "Hombre",
}

export interface StagesFilter {
  interested: "interested";
  initial_contact: "initial_contact";
  presentation: "presentation";
  won: "won";
  lost: "lost";
  quarantine: "quarantine";
  negotiation: "negotiation";
}

export interface UsersTasksPlanned {
  "1": "1";
  "2": "2";
}

export interface GetLeadCaptionArgs {
  branch_filter?: number;
  sex_filter?: SexFilter;
  stages_filter?: StagesFilter;
  campaign_filter?: number;
  is_in_campaign_filter?: boolean;
  users_tasks_planned?: UsersTasksPlanned;
  max_lead?: number;
  search?: string;
}

export const leadsApi = api
  .enhanceEndpoints({
    // Define tags para la invalidación y el refresco de caché
    addTagTypes: ["Leads", "LeadTasks", "LeadStageHistory"],
  })
  .injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
      // Endpoint existente para obtener un lead (según tu código)
      lead: builder.query<Lead, { id: number | string }>({
        query: ({ id }) => ({
          url: `lead/get/${id}`, // URL según tu ejemplo original
          // Si tu API de Laravel es /api/leads/{id}, entonces sería: `leads/${id}`
          method: RequestMethod.GET,
        }),
        // transformResponse: (response: any) => response, // Puedes transformar la respuesta si es necesario
        providesTags: (result, error, { id }) => [{ type: "Leads", id }],
      }),

      // NUEVO: Endpoint para obtener las tareas de un lead
      getLeadTasks: builder.query<LeadTask[], { leadId: number | string }>({
        query: ({ leadId }) => ({
          url: `leads/${leadId}/tasks`,
          method: RequestMethod.GET,
        }),
        providesTags: (result, error, { leadId }) => [
          // Proporciona un tag específico para las tareas de este lead
          { type: "LeadTasks", id: leadId },
        ],
      }),

      // NUEVO: Endpoint para obtener el historial de etapas de un lead
      getLeadStageHistory: builder.query<
        StageHistoryEntry[],
        { leadId: number | string }
      >({
        query: ({ leadId }) => ({
          url: `leads/${leadId}/stage-history`,
          method: RequestMethod.GET,
        }),
        providesTags: (result, error, { leadId }) => [
          // Proporciona un tag específico para el historial de este lead
          { type: "LeadStageHistory", id: leadId },
        ],
      }),

      // NUEVO: Endpoint para agregar una nota a un lead (Mutación)
      addLeadNote: builder.mutation<Lead, AddNoteArgs>({
        query: ({ leadId, notes }: AddNoteArgs) => ({
          url: `leads/${leadId}/notes`,
          method: RequestMethod.POST,
          body: { notes }, // El cuerpo de la petición
        }),
        invalidatesTags: (result, error, { leadId }) => [
          { type: "Leads", id: leadId },
        ],
      }),

      // NUEVO: Endpoint para actualizar la etapa de un lead (Mutación)
      updateLeadStage: builder.mutation<Lead, UpdateStageArgs>({
        query: ({ leadId, stages }) => ({
          url: `leads/${leadId}/stage`,
          method: RequestMethod.PUT,
          body: { stages }, // El cuerpo de la petición
        }),
        // Invalida el tag del lead y su historial de etapas para que se vuelvan a fetchear
        invalidatesTags: (result, error, { leadId }) => [
          { type: "Leads", id: leadId },
          { type: "LeadStageHistory", id: leadId },
        ],
      }),

      ///change stage for lead
      changeStage: builder.mutation({
        query: ({ leadId, stages }) => ({
          url: `leads/change-stage?lead_id=${leadId}&stages=${stages}`,
          method: RequestMethod.PUT,
        }),
        invalidatesTags: (result, error, { leadId }) => [
          { type: "Leads", id: leadId },
          { type: "LeadStageHistory", id: leadId },
        ],
      }),

      //create lead  leads/create-body-captain

      createLead: builder.mutation({
        query: (body) => ({
          url: `leads/create-body-captain`,
          method: RequestMethod.POST,
          body: body,
        }),
        invalidatesTags: (result, error, { leadId, campaign_filter }) => [
          { type: "Leads", id: leadId },
          { type: "LeadStageHistory", id: campaign_filter },
        ],
      }),

      getLeads: builder.query<
        LeadTask[],
        { branch_filter: number | string; campaign_filter: string | number }
      >({
        query: ({ branch_filter, campaign_filter }) => ({
          url: `leads/get-leads?branch_filter=${branch_filter}&campaign_filter=${campaign_filter}`,
          method: RequestMethod.GET,
        }),
        providesTags: (result, error, { branch_filter, campaign_filter }) => [
          { type: "Leads", id: branch_filter },
          { type: "LeadStageHistory", id: campaign_filter },
        ],
      }),

      // NEW ENDPOINTS
      getLeadCaption: builder.query<any, GetLeadCaptionArgs>({
        query: (params: GetLeadCaptionArgs) => {
          return {
            url: `leads/leads-list`,
            method: RequestMethod.GET,
            params: buildQueryParams(params),
          };
        },
      }),

      getBranchByCompany: builder.query<any, Partial<GetLeadCaptionArgs>>({
        query: (params: GetLeadCaptionArgs) => {
          return {
            url: `/leads/branches`,
            method: RequestMethod.GET,
            params: buildQueryParams(params),
          };
        },
      }),

      getCampaigns: builder.query({
        query: () => {
          return {
            url: `leads/campaigns`,
            method: RequestMethod.GET,
          };
        },
      }),

      getTagsMarketing: builder.query({
        query: () => {
          return {
            url: `/leads/tags-marketing`,
            method: RequestMethod.GET,
          };
        },
      }),
    }),
  });

// Exporta los hooks generados automáticamente
export const {
  useLeadQuery,
  useGetLeadTasksQuery, // Hook para obtener tareas
  useLazyGetLeadTasksQuery, // Hook para obtener tareas de forma perezosa (bajo demanda)
  useGetLeadStageHistoryQuery, // Hook para obtener historial de etapas
  useLazyGetLeadStageHistoryQuery, // Hook para obtener historial de forma perezosa
  useAddLeadNoteMutation, // Hook para la mutación de agregar nota
  useUpdateLeadStageMutation, // Hook para la mutación de actualizar etapa
  useChangeStageMutation, // Hook para la mutación de actualizar etapa
  useCreateLeadMutation, // Hook para la mutación de crear cliente
  useGetLeadsQuery,

  // NEW ENDPOINTS
  useGetLeadCaptionQuery, // leads
  useGetBranchByCompanyQuery, // branches
  useGetCampaignsQuery, // campaigns
  useGetTagsMarketingQuery, // tags marketing
} = leadsApi;
